import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useHistory, useParams } from 'react-router';

import VendorsCveStore from 'app/store/VendorsCveStore';
import usePageMeta from 'hooks/use-page-meta';
import useSearchParams from 'hooks/use-search-params';
import useStores from 'hooks/use-stores';
import Heading from 'main/components/Heading';
import DataTable from 'pages/Search/components/DataTable';
import Layout from 'pages/Search/components/Layout';
import LetterFilter from 'pages/Search/components/LetterFilter';
import RedirectLink from 'pages/Search/components/RedirectLink';
import { NONE, ROUTES } from 'pages/Search/constants';
import { noneIf, normalizeName } from 'pages/Search/utils';

import useStyles from './styles';

const Vendors = () => {
    const { classes } = useStyles();
    const history = useHistory();
    const { vendorsCveStore } = useStores();

    /** @type {Record<string, string>} */
    const { letter, page } = useParams();

    const hasMetaPagination = Number(page || 1) > 1;
    const pageMeta = hasMetaPagination ? ' - Page ' + page : '';

    usePageMeta({
        title: `Vulnerabilities by vendors${letter ? ' - ' + letter.toUpperCase() : ''}${pageMeta} | Vulners.com`,
        description: `Vulnerabilities by vendor names${letter ? 'starting with' + letter : ''}${pageMeta}`
    });

    const [{ pageSize }, setSearchParams] = useSearchParams();

    /** @type {React.RefObject<{scrollTop: () => void} | null>} */
    const layoutRef = useRef(null);

    const setPath = useCallback(
        ({ letter, page }) =>
            history.push({ pathname: ROUTES.VENDORS({ page, letter }), search: history.location.search }),
        [history]
    );

    const getOptionHref = useCallback(
        (letter) => ROUTES.VENDORS({ page: null, letter }).concat(history.location.search),
        [history.location.search]
    );

    const getPaginationHref = useCallback(
        (page) => ROUTES.VENDORS({ page: noneIf(page, 1), letter }).concat(history.location.search),
        [history.location.search, letter]
    );

    useEffect(() => {
        layoutRef.current?.scrollTop();
        vendorsCveStore
            .setParams({
                letter: String(letter || ''),
                page: Number(page || 1),
                pageSize: Number(pageSize || 100)
            })
            .loadItems();
    }, [letter, page, pageSize, vendorsCveStore]);

    const tableData = useMemo(
        () => [
            ['Vendor Name', 'Products', 'Vulnerabilities'],
            ...vendorsCveStore.list.map(({ vendorName, productsCount, cveCount }) => [
                <RedirectLink
                    key={`${vendorName}-vulnerabilities`}
                    to={ROUTES.VENDOR_CVE({ page: null, vendorName })}
                    label={normalizeName(vendorName) || NONE}
                    active={Boolean(vendorName)}
                />,
                <RedirectLink
                    key={`${vendorName}-products`}
                    to={ROUTES.VENDOR({ page: null, vendorName })}
                    label={productsCount}
                    active={Boolean(vendorName)}
                />,
                cveCount
            ])
        ],
        [vendorsCveStore.list]
    );

    const handlePageChange = useCallback((page) => setPath({ page, letter: vendorsCveStore.letter }), [
        vendorsCveStore,
        setPath
    ]);
    const handleLetterSelect = useCallback((letter) => setPath({ page: null, letter }), [setPath]);
    const handlePageSizeChange = useCallback(
        (pageSize) => setSearchParams({ pageSize: noneIf(pageSize, VendorsCveStore.DEFAULT_PAGE_SIZE) }),
        [setSearchParams]
    );

    return (
        <Layout
            ref={layoutRef}
            scrollable
            main={
                <>
                    <Heading text={letter ? `Vendors – ${letter.toUpperCase()}` : 'Vendors'} />
                    <LetterFilter
                        selected={vendorsCveStore.letter}
                        getOptionHref={getOptionHref}
                        onSelect={handleLetterSelect}
                        className={classes.letters}
                    />
                    <DataTable
                        data={tableData}
                        loading={vendorsCveStore.isLoading}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        getPaginationHref={getPaginationHref}
                        page={vendorsCveStore.page}
                        pageSize={vendorsCveStore.pageSize}
                        totalCount={vendorsCveStore.totalCount}
                    />
                </>
            }
        />
    );
};

export default observer(Vendors);
